import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { SOLUTIONS } from "@/data/content";

const List = ({ title, items, testId }) => (
  <div>
    <h4 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">{title}</h4>
    <ul className="mt-3 space-y-2" data-testid={testId}>
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2 text-sm text-slate-300">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          {it}
        </li>
      ))}
    </ul>
  </div>
);

export default function Solutions() {
  useSEO(
    "Solutions | Business Support, Customer Operations, Sales & IT Teams | Synergy Outsourcing",
    "Managed teams for business operations, customer support, lead generation, social media, finance, IT and professional services. Trusted by businesses across Australia, the US and Canada."
  );
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  return (
    <>
      <PageHero
        eyebrow="Solutions"
        lines={["Managed teams for the", "work that moves", "your business."]}
        copy="From day-to-day operations to specialist professional capability: dedicated people, structured delivery, one accountable partner."
      />
      {SOLUTIONS.map((s, idx) => (
        <section
          key={s.id}
          id={s.id}
          data-testid={`solution-${s.id}`}
          className={`scroll-mt-24 py-14 sm:py-16 ${idx % 2 === 1 ? "bg-ink-2/40" : ""}`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-start gap-12 lg:grid-cols-12">
              <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <Reveal>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft text-brand">
                    <s.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <p className="mt-5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand">{s.name}</p>
                  <h2 className="mt-3 font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl">
                    {s.headline}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-mist">{s.intro}</p>
                  <p className="mt-3 text-base leading-relaxed text-mist">{s.provide}</p>
                  <Link
                    to="/get-your-team"
                    data-testid={`solution-cta-${s.id}`}
                    className="group mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
                  >
                    Explore {s.name}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Reveal>
              </div>
              <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.15}>
                  <div className="grid gap-8 rounded-2xl border border-white/10 bg-ink-2 p-8 sm:grid-cols-2">
                    <List title="Typical roles" items={s.roles} testId={`solution-roles-${s.id}`} />
                    <List title="Example responsibilities" items={s.responsibilities} testId={`solution-resp-${s.id}`} />
                    <List title="Who it's suited for" items={s.suited} testId={`solution-suited-${s.id}`} />
                    <List title="How the engagement works" items={s.engagement} testId={`solution-engagement-${s.id}`} />
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}
      <CTASection />
    </>
  );
}
